/**
 * `RenderSkip` — slice **L5** (LR6 "render skip", the ledger's **first real
 * consumer**, running **dark-first**) of the row-identity ledger design
 * (§4 LR6, §7 row L5). It is the pure, DOM-free decision/record/referee core the
 * sheets-**ui** binder consumes: at bind time it compares the row's last-rendered
 * output versions against the ledger's current versions and decides *repaint vs
 * skip* (north-star 01 Layer 1).
 *
 * ## The compare key is the row's OWN output node — never its inputs
 *
 * LR6 keys on the **consumer-visible output** node — `data:<Address>` (the row's
 * own cells, bumped by L2 whenever those cells are WRITTEN) plus `attr:<serial>`
 * (the row's attribute bag) — resolved by the probe (`AvailabilityProbe`,
 * `SourceRowCellData.currentAvailability`, which routes through
 * `LedgerBinding.resolveOutputAddress` — the same classification the cell
 * write's bump uses, so for a data row the compare node is guaranteed identical
 * to the bumped node, and a placement-only row says so by name — no data node,
 * nothing to compare, never skip; the write path's total fallback node must
 * never be compared against, since nothing ever writes it).
 *
 * It is **NOT** a walk over upstream inputs. A derived/formula row's output node
 * is bumped by its *recompute's* writes, so freshness is correct: while an input
 * has changed but the recompute has not yet written the derived output, the
 * output version is unchanged and the row correctly stays skip-eligible (nothing
 * to repaint yet); the recompute's write then bumps the output and the next bind
 * repaints. Keying on the **inputs** instead would *skip stale derived UI* — a
 * recompute that changes the output without moving the specific recorded input
 * (e.g. a range-load recompute) would read "input unchanged ⟹ skip" and leave the
 * changed output unpainted. Derived freshness stays signature-gated until L6, so
 * L5 deliberately compares only the output node.
 *
 * ## Dark-first — the skip is flag-gated; the decision + referee run always
 *
 * The skip is gated by the SDK 2.0 switch, the sheet's
 * `registries.render.live()` (`RenderFlags`; no env vars). With the switch off, `decide`
 * still runs and is LOGGED via the referee, but `shouldSkip` returns false so
 * the bind always proceeds. With it on, an agreeing decision actually
 * short-circuits the bind.
 * While dark, the referee (`refereeAfterBind`) checks every *would-skip* decision
 * against ground truth (the bind ran anyway — did it change the rendered output?);
 * a divergence (`wouldSkip && changedOutput`) is counted loud, exactly the
 * LI2/L4 referee pattern. The flag flips on only once the referee shows zero
 * divergences across the corpus.
 *
 * This module value-imports **nothing** sheet-related (only `Tracing` +
 * `RowLocalId` type-only), so it is TDZ-inert — no variant-chain cycle risk.
 */

import type { Option } from "../core/option.ts";
import { Tracing } from "@tento-core/tracing";

import type { RenderFlags } from "./render-flags.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";

/** Flush a `ledger/lr6` breadcrumb every this-many divergences. */
const DIVERGENCE_FLUSH_BATCH = 1;

/**
 * The DOM-free repaint-or-skip decision the sheets-ui binder takes per row, comparing its
 * last-rendered output versions with the ledger's.
 */
export class RenderSkip {
  /**
   * Per-row rendered-version record: the `(values, attr)` versions the cell
   * currently bound to this row was last fully rendered at. Keyed by the
   * in-process `RowLocalId` (promote-stable placement identity), mirroring the
   * binder's own `previousRenderState` / `rowModes` maps.
   */
  readonly #records = new Map<RowLocalId, RenderSkip.Versions>();

  #decisions = 0;
  #wouldSkips = 0;
  #skips = 0;
  #divergences = 0;

  /** The sheet's render-pipeline flags (`SheetRegistries.render`). */
  readonly #flags: RenderFlags;

  readonly #mode: RenderSkip.Mode;
  readonly #probe: RenderSkip.Probe;
  constructor(
    probe: RenderSkip.Probe,
    /** The sheet's render-pipeline flags (`SheetRegistries.render`). */
    flags: RenderFlags,
    /**
     * Whether a would-skip actually skips (`RenderSkip.Mode`): `"follow-flags"`
     * reads `flags.live()` fresh at each decision; `"live"` / `"dark"` pin
     * this instance regardless of it (the CLI mints `"live"`, tests pin both
     * ways).
     */
    mode: RenderSkip.Mode,
  ) {
    this.#probe = probe;
    this.#mode = mode;
    this.#flags = flags;
  }

  /**
   * The LR6 decision for `id` (LR6 compare). `skip ⟺` the row has a prior record
   * (was fully bound before) **and** every consumed facet's current version
   * equals the record.
   *
   * Seam 12 — the conservative default is a **choice per case**, not one `null`
   * standing for two situations. The probe now reports a
   * `RenderSkip.Availability` kind, and each kind refuses (or allows) the skip
   * on its own stated grounds:
   *
   *  - `placement-only` — the row owns no `data:` node (a system / pending /
   *    template row positions data owned elsewhere). Nothing to compare, and the
   *    write path's total fallback node is a frozen 0 that would read "unchanged"
   *    forever. **Never skip.**
   *  - `no-ledger` — no ledger is attached at all (standalone / test store), so
   *    no version ever moves. **Never skip.**
   *  - `versions` — real output versions; compare them against the record.
   *
   * The refusing kind is recorded on the decision (`reason`), so a divergence
   * breadcrumb or a referee can say WHICH case declined rather than "null".
   * Pure w.r.t. the ledger — only reads versions, never bumps.
   */
  decide(id: RowLocalId): RenderSkip.Decision {
    const availability = this.#availabilityOf(id);
    const current = RenderSkip.versionsOf(availability);
    const record = this.#records.get(id) ?? null;
    const boundBefore = record != null;
    let wouldSkip: boolean;
    switch (availability.kind) {
      case "placement-only":
      case "no-ledger":
        // Both refuse — but they refuse for different, now-nameable reasons.
        wouldSkip = false;
        break;
      case "versions":
        wouldSkip = record != null &&
          record.values === availability.values &&
          record.attr === availability.attr;
        break;
    }
    this.#decisions += 1;
    if (wouldSkip) this.#wouldSkips += 1;
    return { boundBefore, current, wouldSkip, reason: availability.kind };
  }

  /**
   * Read the probe's availability, tolerating a legacy `VersionProbe` that only
   * answers `currentVersions`. Such a probe cannot tell "placement-only" from
   * "no ledger" — its `null` is exactly the conflation this module removes — so its
   * refusal is reported as `no-ledger` (the weaker claim: we do not know that a
   * node exists). Implement `currentAvailability` to get the real kind.
   */
  #availabilityOf(id: RowLocalId): RenderSkip.Availability {
    const probe = this.#probe;
    if ("currentAvailability" in probe) return probe.currentAvailability(id);
    const versions = probe.currentVersions(id);
    return versions == null
      ? { kind: "no-ledger" }
      : { kind: "versions", values: versions.values, attr: versions.attr };
  }

  /**
   * Whether a *would-skip* decision should actually short-circuit the bind. The
   * effective enable is the instance's `mode`: a `"live"` / `"dark"` pin
   * (captured at cursor mint) wins; `"follow-flags"` reads the sheet's
   * SDK 2.0 switch, read fresh here so a mid-session toggle takes effect on the
   * next bind. While the switch is dark this returns false and the caller binds
   * anyway. Counts the skips it authorizes.
   */
  shouldSkip(decision: RenderSkip.Decision): boolean {
    const enabled = this.#mode === "follow-flags" ? this.#flags.live() : this.#mode === "live";
    const skip = decision.wouldSkip && enabled;
    if (skip) this.#skips += 1;
    return skip;
  }

  /**
   * Record that the cell now renders `id` at the current output versions — call
   * after a full successful bind so the *next* bind's `decide` can skip. Reads
   * the versions fresh (binding never bumps the ledger, so this equals the
   * decide-time read). An unresolvable node evicts the record (never skip).
   */
  recordNow(id: RowLocalId): void {
    const availability = this.#availabilityOf(id);
    if (availability.kind !== "versions") {
      // Placement-only and no-ledger both evict, for the same downstream effect
      // (never skip next bind) but on their own grounds — see `decide`.
      this.#records.delete(id);
      return;
    }
    this.#records.set(id, { values: availability.values, attr: availability.attr });
  }

  /**
   * The **LR6 referee** (design §8, the LI2/L4 pattern): while dark, a
   * would-skip decision whose bind *then ran anyway* MUST have produced a no-op.
   * `changedOutput` is ground truth (the cell's output fingerprint moved).
   * `wouldSkip && changedOutput` is a divergence — the version compare would have
   * skipped a real repaint — counted loud in a bounded `Tracing.log`. Never a
   * behavior change (observe only).
   */
  refereeAfterBind(decision: RenderSkip.Decision, changedOutput: boolean): void {
    if (!decision.wouldSkip || !changedOutput) return;
    this.#divergences += 1;
    if (this.#divergences % DIVERGENCE_FLUSH_BATCH === 0) {
      Tracing.log("ledger/lr6-divergence", {
        // Bounded, JSON-safe — counts + the versions only, never row data.
        divergences: this.#divergences,
        values: decision.current?.values ?? -1,
        attr: decision.current?.attr ?? -1,
        // Seam 12: which availability kind the decision was made against. A
        // would-skip can only arise from `versions`, so anything else here is
        // itself the finding.
        reason: decision.reason,
      });
    }
  }

  /** Drop `id`'s record (recycling / partial-bind reset — never skip next). */
  evict(id: RowLocalId): void {
    this.#records.delete(id);
  }

  /** Drop every record (mirrors the binder's `clearRowModes` bulk reset). */
  clear(): void {
    this.#records.clear();
  }

  /** Whether `id` currently has a rendered-version record (test/introspection). */
  has(id: RowLocalId): boolean {
    return this.#records.has(id);
  }

  /** Pull-based aggregate counters for tests. */
  snapshot(): RenderSkip.Counters {
    return {
      decisions: this.#decisions,
      wouldSkip: this.#wouldSkips,
      skipped: this.#skips,
      divergences: this.#divergences,
    };
  }
}

/**
 * Types and values that go with `RenderSkip`: `Mode`, `Versions`,
 * `Availability`, `Reason`, `versionsOf`, `VersionProbe`, and 4 more.
 */
export namespace RenderSkip {
  /**
   * Whether a would-skip decision actually skips the bind: `"live"` always
   * (the decision is consumed), `"dark"` never (decided, counted and refereed,
   * but the bind proceeds), `"follow-flags"` as the sheet's
   * `registries.render.live()` says at each decision.
   */
  export type Mode = "live" | "dark" | "follow-flags";

  /**
   * The current versions of a row's OUTPUT nodes: `values` = its own
   * `data:<Address>` cell node, `attr` = its `attr:<serial>` bag node — both at
   * the `VALUES` facet (design §7.1). A missing node reads 0.
   */
  export interface Versions {
    readonly values: number;
    readonly attr: number;
  }

  /**
   * **Seam 12** — what the probe can say about a row's output versions. The
   * predecessor was `Option<Versions>`, whose `null` meant *either* "this row
   * owns no data node" *or* "this store has no ledger at all". Both refuse the
   * skip, but they are different facts about the system: the first is a
   * permanent property of the row's classification, the second is a property of
   * how the store was constructed (a standalone / test store, or an accessor
   * handed an un-instrumented canonical store). Collapsing them made every
   * refusal unattributable — you could not tell a correctly-classified
   * placement-only row from a ledger that was never wired.
   *
   * `Versions` stays the payload; the `versions` arm carries it inline.
   */
  export type Availability =
    | ({ readonly kind: "versions" } & Versions)
    /** The row owns no `data:` node (system / pending / template / container). */
    | { readonly kind: "placement-only" }
    /** No ledger is attached to this store — nothing versions anything. */
    | { readonly kind: "no-ledger" };

  /** Which `Availability` arm a decision was made against. */
  export type Reason = Availability["kind"];

  /** The versions an `Availability` carries, or `null` when it carries none. */
  export function versionsOf(availability: Availability): Option<Versions> {
    return availability.kind === "versions"
      ? { values: availability.values, attr: availability.attr }
      : null;
  }

  /**
   * The minimal per-row version read the skip logic needs — implemented rows-side
   * by `SourceRowCellData.currentVersions` (which owns the `LedgerBinding` /
   * `LedgerKeys` / `LedgerReader` key resolution). `null` when the row has no
   * output node or no ledger is attached (dark / standalone).
   *
   * Retained as the **compatibility** shape: consumers outside this package
   * (sheets-ui's live scene runner, the commit-bundle fixtures) already hold a
   * `currentVersions`-only probe, and `RenderSkip` still accepts one. It cannot
   * distinguish the two refusals — see `AvailabilityProbe`.
   */
  export interface VersionProbe {
    currentVersions(id: RowLocalId): Option<Versions>;
  }

  /**
   * The seam-12 probe: reports WHICH of the three cases holds. Extends
   * `VersionProbe` so an availability-aware probe remains usable everywhere the
   * old shape was. `SourceRowCellData` implements this.
   */
  export interface AvailabilityProbe extends VersionProbe {
    currentAvailability(id: RowLocalId): Availability;
  }

  /** What `RenderSkip` accepts — either probe shape. */
  export type Probe = VersionProbe | AvailabilityProbe;

  /** One bind-time LR6 decision. */
  export interface Decision {
    /** Whether the row had a prior rendered-version record. */
    readonly boundBefore: boolean;
    /**
     * The row's current output versions, or `null` when unresolvable. Kept
     * byte-compatible for existing consumers (`default-binder`, the referee);
     * `reason` is what says *why* it is `null`.
     */
    readonly current: Option<Versions>;
    /** Whether the row's output is unchanged since its last render (skip-eligible). */
    readonly wouldSkip: boolean;
    /**
     * Seam 12 — which availability kind this decision was made against, i.e.
     * which case refused the skip when one did. `versions` means the compare
     * actually ran.
     */
    readonly reason: Reason;
  }

  /** Aggregate-only observability (the L2 bumper pattern). */
  export interface Counters {
    /** Total `decide` calls. */
    readonly decisions: number;
    /** Of those, decisions that were skip-eligible. */
    readonly wouldSkip: number;
    /** Of the would-skips, ones the flag actually authorized to short-circuit. */
    readonly skipped: number;
    /** Would-skip decisions whose bind then changed output — a routing bug. */
    readonly divergences: number;
  }
}
